import { EmptyStateResponse } from "@website/accommodation-ui/tour-empty-state";
import { SORT_OPTIONS } from "@website/accommodation-ui/tour-result-header/tour-sort-view";
import { CityType } from "@website/accommodation-ui/tour-search-wrapper";
import { fetcher } from "@website/fetcher/fetcher";
import { getChannel } from "@website/utils/channel/get-channel";
import { Channels } from "@website/utils/channel/types";
import { getFormattedFutureDate } from "@website/utils/date-format";
import isEmpty from "lodash-es/isEmpty";
import omit from "lodash-es/omit";
import { ParsedUrlQuery } from "node:querystring";
import {
  GET_BANNERS_URL,
  HOTELS_RESULT_URL_V2
} from "../../../constant/api-addresses";
import { ResponseSearchResultType, SearchRequestData } from "./types";

export const searchSolutionsService = ({
  origin,
  destination,
  departureDate,
  returnDate,
  rooms,
  filter,
  aid
}: SearchRequestData) =>
  fetcher({
    url: HOTELS_RESULT_URL_V2,
    method: "POST",
    headers: {
      aid,
      clientId: getChannel() as Channels
    },
    next: {
      revalidate: 60 * 60,
      tags: [
        HOTELS_RESULT_URL_V2,
        destination,
        origin,
        departureDate,
        returnDate
      ]
    },
    data: {
      origin,
      destination,
      departureDate,
      returnDate,
      rooms,
      filter: {
        ...filter,
        page_size: 10,
        sort: filter.sort ?? SORT_OPTIONS.RECOMMENDED
      }
    }
  }).then(({ data }: ResponseSearchResultType) => data);

export const fetchSearchResults = ({ query }: { query: ParsedUrlQuery }) => {
  const { dest, origin } = query as Record<string, string>;
  if (!isEmpty(omit(query, ["origin", "dest"]))) return null;

  return searchSolutionsService({
    origin,
    destination: dest,
    departureDate: getFormattedFutureDate(3),
    returnDate: getFormattedFutureDate(5),
    rooms: [{ adultCount: 2, children: [] }],
    filter: { page: 1 }
  });
};

export const fetchBannersData = (
  page: "Citypage" | "resultPage",
  destination: string,
  origin: string
) =>
  fetcher({
    url: GET_BANNERS_URL,
    method: "POST",
    data: {
      channel: getChannel() === "website" ? "web" : getChannel(),
      product: "tour",
      page,
      extraData: {
        destination,
        origin
      }
    },
    next: {
      revalidate: 300,
      tags: [GET_BANNERS_URL, page, destination]
    }
  })
    .then(({ data }) => data)
    .catch(() => []);

export const fetchCitySuggestions = (type: "origin" | "dest") => {
  const url = `${process.env.NEXT_PUBLIC_BASE_URL_API}listing/cities/suggestion?t=${type}`;
  return fetcher({
    url: url,
    method: "GET",
    next: {
      tags: [url],
      revalidate: 120
    }
  })
    .then(({ data }: { data: Array<CityType> }) => data)
    .catch(() => []);
};

export const fetchEmptyStates = (
  destination: string,
  origin: string,
  stDate: string,
  rtDate: string,
  passengers: Array<{ adultCount: number; children: Array<number> }>
) => {
  const url = `${process.env.NEXT_PUBLIC_BASE_URL_API}recommender/recommendations`;
  return fetcher({
    url: url,
    method: "POST",
    headers: {
      clientId: getChannel() as Channels
    },
    data: {
      origin: origin,
      destination: destination,
      departureDate: stDate,
      returnDate: rtDate,
      rooms: passengers
    }
  })
    .then(({ data }: { data: Array<EmptyStateResponse> }) => data)
    .catch(() => [] as Array<EmptyStateResponse>);
};

export const fetchInstalmentWallet = (): Promise<{
  isEligible: boolean;
  showDownPayment: boolean;
  markup: number;
}> =>
  fetcher({
    url: `${process.env.NEXT_PUBLIC_BASE_URL_GATEWAY}/hotel/api/v2/instalment/wallet`,
    method: "GET",
    headers: {
      product: "TOUR"
    }
  }).then(({ data }) => ({
    isEligible: data?.is_eligible,
    showDownPayment: data?.show_down_payment,
    markup: data?.markup
  }));
